import React, {
    useCallback,
    useContext, useMemo
} from 'react';
import _ from 'lodash';
import { IntlContext, TranslatorContext } from '@jutro/locale';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { SgNumberUtil } from 'saga-modules';
import { Link, ModalNextProvider } from '@jutro/components';
import SgSagaPlusTermsModal from 'sg-saga-plus-terms-modal';
import metadata from './SgImportantStatementsAndDeclarations.metadata.json5';
import messages from './SgImportantStatementsAndDeclarations.messages';

function SgImportantStatementsAndDeclarations(props) {
    const {
        modelVM,
        showFullStatements,
        premium,
        chargeForCredit,
        isMonthly,
        statementsSectionHeader,
        showCancellationFeeInfo,
        lobBulletPoints,
        isShortFormat,
        sagaPlusPolicyBook,
        showFixedPriceInfo,
        bulletpointMessageShort,
        insuranceProductName,
        feeAmount,
        isAprZero
    } = props;

    const translator = useContext(TranslatorContext);
    const intl = useContext(IntlContext);
    const { productCode } = modelVM.value.baseData;

    const CANCELLATION_FEES = {
        PrivateMotor: 50,
        PrivateHome: 35
    };

    const payableBulletpointMessage = useMemo(() => {
        const totalWithoutFee = {
            amount: premium.amount - chargeForCredit.amount - feeAmount,
            currency: 'gbp'
        };
        const messageParams = {
            insuranceType: insuranceProductName,
            totalPayable: SgNumberUtil.getCurrencyValue(intl, premium),
            insurancePremium: SgNumberUtil.getCurrencyValue(intl, totalWithoutFee),
            fee: feeAmount,
            chargeForCredit: SgNumberUtil.getCurrencyValue(intl, chargeForCredit, true)
        };

        if (isMonthly) {
            return chargeForCredit && !isAprZero
                ? translator(messages.MonthlyWithApr, messageParams)
                : translator(messages.MonthlyWithoutApr, messageParams);
        }
        if (bulletpointMessageShort) {
            return translator(messages.AnnualShortMsg, messageParams);
        }
        return translator(messages.Annual, messageParams);
    }, [premium, chargeForCredit, feeAmount, insuranceProductName, intl, isMonthly, bulletpointMessageShort, translator, isAprZero]);

    const openTermsAndConditionsModal = useCallback((event) => {
        if (event) {
            event.preventDefault();
        }
        const modalProps = {
            productCode,
            sagaPlusPolicyBook
        };

        ModalNextProvider.showModal(
            <SgSagaPlusTermsModal {...modalProps} />
        );
    }, [productCode, sagaPlusPolicyBook]);

    const termsAndConditionsLink = useMemo(() => {
        return <Link href="/" onClick={openTermsAndConditionsModal}>{translator(messages.PlusTermsAndConditionsLink)}</Link>;
    }, [openTermsAndConditionsModal, translator]);

    const overrideProps = {
        statementsSectionHeader: {
            content: translator(statementsSectionHeader)
        },
        lobBulletpoints: {
            visible: !_.isNil(lobBulletPoints) ? '' : false,
            content: lobBulletPoints
        },
        confirmQuestionsBulletpoint: {
            name: 'confirmQuestionsBulletpoint',
            content: bulletpointMessageShort
                ? translator(messages.ConfirmQuestionsBulletpointShort)
                : translator(messages.ConfirmQuestionsBulletpoint)
        },
        payableBulletpoint: {
            visible: !isShortFormat ? '' : false,
            name: 'payableBulletpoint',
            content: payableBulletpointMessage
        },
        cancellationBulletpoint: {
            visible: !isShortFormat && showCancellationFeeInfo && !isAprZero ? '' : false,
            name: 'cancellationBulletpoint',
            content: translator(messages[`CancellationBulletpoint${productCode}`],
                { cancellationFee: CANCELLATION_FEES[productCode] })
        },
        plusBulletpoint: {
            visible: !isShortFormat && showFixedPriceInfo ? '' : false,
            name: 'plusBulletpoint',
            content: (
                <>
                    {translator(messages[`PlusBulletpoint${productCode}`])}
                    {termsAndConditionsLink}
                </>
            )
        },
        validInUKBulletpoint: {
            visible: showFullStatements ? '' : false,
            name: 'validInUKBulletpoint',
            content: translator(messages.ValidInUKBulletpoint)
        }
    };

    const resolvers = {
    };

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

SgImportantStatementsAndDeclarations.defaultProps = {
    showFullStatements: true,
    chargeForCredit: { amount: 0 },
    statementsSectionHeader: messages.StatementsSectionHeader,
    isShortFormat: false,
    showCancellationFeeInfo: false,
    showFixedPriceInfo: false,
};

export default SgImportantStatementsAndDeclarations;
